import React, { memo, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import { useLocation } from 'react-router-dom'
import { Typography } from '@mui/material'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import { useTranslation } from 'react-i18next'
import { useDateRangeWithURL, useDashboardData, useDrawerState, useVoting, useDrawerURL } from '../../hooks'
import useAuthz from '../../hooks/useAuthz'
import { authenticatedFetch } from '../../services/api/apiClient'
import { apiEndpoints } from '../../consts'
import logger from '../../utils/logger'
import { useAutoSync } from '../../hooks/useAutoSync'
import { getCurrentDate, formatCallDateET } from '../../services/date'
import {
  DashboardHeader,
  DateRangeController,
  ReportDataDisplay,
  MetricsContent,
  ErrorBoundary,
  LoadingSkeleton,
  PageLayout,
  PageHeader
} from '../../components'
import { DrawerCard } from '../../components/DrawerComponents'

/**
 * Dashboard component displays call analytics and data visualization
 */
const Dashboard = ({
  startDate: initialStart,
  endDate: initialEnd,
  company: initialCompany,
  isAuthenticated,
  userId,
  campaignFilter
}) => {
  useAutoSync()

  const { isAdmin } = useAuthz()
  const { t } = useTranslation()
  const location = useLocation()

  const {
    startDate,
    endDate,
    startDateString,
    endDateString,
    handleStartDateChange,
    handleEndDateChange
  } = useDateRangeWithURL(initialStart, initialEnd)

  const {
    dispositionExpanded,
    metricsDrawerOpen,
    shouldShowMetricsDrawer,
    handleDispositionToggle
  } = useDrawerState(location)

  const {
    planSize,
    planSizeLoading,
    metrics,
    metricsLoading,
    chartData,
    processedReportData,
    actualCampaignName,
    isDataLoading
  } = useDashboardData(
    startDate,
    endDate,
    startDateString,
    endDateString,
    initialCompany,
    campaignFilter
  )

  const { rowsList, dispositionMap, timeString } = processedReportData

  const baseVotingProps = useVoting(userId, rowsList)
  const { openDrawerWithRow } = baseVotingProps

  const openingFromURLRef = useRef(false)

  const handleOpenFromURL = useCallback(async (callId) => {
    openingFromURLRef.current = true
    try {
      const resp = await authenticatedFetch(
        `${apiEndpoints.calls}?callId=${encodeURIComponent(callId)}&limit=1`
      )
      if (!resp.ok) return
      const json = await resp.json()
      const calls = json.calls || json.data
      if (!calls?.length) return

      const call = calls[0]
      const row = {
        id: call.call_id,
        0: formatCallDateET(call.call_date),
        1: call.direction === 'Inbound' ? (call.caller_number || call.dnis || 'N/A') : (call.dnis || call.caller_number || 'N/A'),
        3: call.call_id,
        4: call.direction || 'Inbound',
        5: call.campaign_name || 'Unknown',
        6: call.agent_name || 'Unknown',
        9: call.handle_time || 0,
        13: call.disposition || 'Unknown',
        14: call.call_notes || '',
        17: call.caller_name || '',
        18: call.caller_company || '',
        caller_email: call.caller_email || '',
        ai_transcript: call.ai_transcript,
        ai_summary: call.ai_summary,
        sentiment_analysis: call.sentiment_analysis,
        qa_evaluation: call.qa_evaluation
      }
      openDrawerWithRow(row)
    } catch (err) {
      logger.error('Error opening call from URL:', err)
    } finally {
      openingFromURLRef.current = false
    }
  }, [openDrawerWithRow])

  const { openDrawerWithURL, closeDrawerWithURL } = useDrawerURL('call', {
    drawerOpen: baseVotingProps.drawerOpen,
    onOpenFromURL: handleOpenFromURL,
    onClose: baseVotingProps.onCloseDrawer
  })

  const votingProps = {
    ...baseVotingProps,
    onRowClick: (row) => {
      baseVotingProps.onRowClick(row)
      if (!openingFromURLRef.current) {
        const callId = row[3]
        if (callId) openDrawerWithURL(callId)
      }
    },
    onCloseDrawer: () => {
      baseVotingProps.onCloseDrawer()
      closeDrawerWithURL()
    }
  }

  const dateRangeControllerComponent = (
    <ErrorBoundary componentName='DateRangeController'>
      {isDataLoading
        ? (
          <LoadingSkeleton type='dateRange' />
          )
        : (
          <DateRangeController
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={handleStartDateChange}
            onEndDateChange={handleEndDateChange}
            dispositionExpanded={dispositionExpanded}
            onDispositionToggle={handleDispositionToggle}
            disabled={isDataLoading}
          />
          )}
    </ErrorBoundary>
  )

  const reportDataComponent = (
    <ErrorBoundary componentName='ReportDataDisplay'>
      {isDataLoading
        ? (
          <LoadingSkeleton type='table' itemCount={5} />
          )
        : (
          <ReportDataDisplay
            isAuthenticated={isAuthenticated}
            rowsList={rowsList}
            timeString={timeString}
            dispositionMap={dispositionMap}
            metrics={metrics}
            metricsLoading={metricsLoading}
            userId={userId}
            dateRangeStart={startDate}
            dateRangeEnd={endDate}
            containerWidth='100%'
            isLoading={isDataLoading}
            {...votingProps}
          />
          )}
    </ErrorBoundary>
  )

  return (
    <PageLayout>
      <PageHeader
        title={isAdmin ? t('navigation.vetaMainline') : t('navigation.callDashboard')}
        subtitle={t('analytics.subtitle')}
        alert={
          <Alert severity='info'>
            <AlertTitle>{t('common.info')}</AlertTitle>
            <Typography variant='body2'>{t('analytics.infoMessage')}</Typography>
          </Alert>
        }
      />

      {/* Dashboard Header with Error Boundary */}
      <ErrorBoundary componentName='DashboardHeader'>
        {isDataLoading
          ? (
            <LoadingSkeleton type='header' />
            )
          : (
            <DashboardHeader
              campaignName={actualCampaignName}
              isLoading={isDataLoading}
              startDate={startDate}
              endDate={endDate}
            />
            )}
      </ErrorBoundary>

      {/* Inline collapsible analytics section */}
      {shouldShowMetricsDrawer && (
        <DrawerCard
          title='Analytics'
          icon='chart-bar'
          collapsible
          defaultExpanded={metricsDrawerOpen}
          fullWidth
          variant='featured'
        >
          <ErrorBoundary componentName='MetricsContent'>
            {metricsLoading
              ? (
                <LoadingSkeleton type='metrics' />
                )
              : (
                <MetricsContent
                  metrics={metrics}
                  metricsLoading={metricsLoading}
                  dateRangeStart={startDate}
                  dateRangeEnd={endDate}
                  today={getCurrentDate().toISOString()}
                  planSize={metrics?.planMinutes || planSize}
                  planSizeLoading={planSizeLoading}
                  chartData={chartData}
                  campaignFilter={campaignFilter}
                  onStartDateChange={handleStartDateChange}
                  onEndDateChange={handleEndDateChange}
                />
                )}
          </ErrorBoundary>
        </DrawerCard>
      )}

      {dateRangeControllerComponent}
      {reportDataComponent}
    </PageLayout>
  )
}

Dashboard.propTypes = {
  startDate: PropTypes.object,
  endDate: PropTypes.object,
  company: PropTypes.string,
  isAuthenticated: PropTypes.bool,
  userId: PropTypes.string,
  campaignFilter: PropTypes.string
}

export default memo(Dashboard)
